import React from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { observer } from "mobx-react-lite";
import CustomTypography from "../common/form/CustomTypography";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { StyledTableRow } from "../../../styles/Shipmentstyles";
import { useStores } from "../../../use-stores";
import type { SwagstoreWalletTransaction } from "../../../models/Swagstore/SwagstoreWallet";

interface WalletHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** Above AppBar (`theme.zIndex.drawer + 1`). */
const WALLET_HISTORY_DRAWER_Z_OFFSET = 2000;

const formatInr = (value: number) =>
  `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const displayOrDash = (value: string | null | undefined) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : "—";
};

const formatSourceLabel = (source: string | null) => {
  if (!source) return "—";
  const normalized = source.toLowerCase();
  if (normalized === "admin_manual") return "Admin credit";
  if (normalized === "razorpay") return "Razorpay";
  if (normalized === "debit" || normalized === "order_debit") return "Debit";
  return source;
};

const WalletHistoryDrawer: React.FC<WalletHistoryDrawerProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const { swagStoreStore } = useStores();

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    swagStoreStore.setWalletTransactionsPage(page);
    swagStoreStore.fetchWalletTransactions(page).catch(() => {});
  };

  const renderAmount = (row: SwagstoreWalletTransaction) => {
    const isCredit = row.type === "credit";
    const prefix = isCredit ? "+" : "-";
    return (
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          color: isCredit
            ? theme.palette.success.main
            : theme.palette.error.main,
        }}
      >
        {prefix}
        {formatInr(Number(row.amount) || 0)}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 720 },
          maxWidth: { xs: "100vw", sm: 720 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) =>
            muiTheme.zIndex.drawer + WALLET_HISTORY_DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + WALLET_HISTORY_DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" gap={1.5} alignItems="center">
          <AccountBalanceWalletOutlinedIcon color="primary" />
          <Box>
            <CustomTypography variant="heading5">
              Transaction History
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, display: "block" }}
            >
              View and manage all your wallet transactions.
            </CustomTypography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        {swagStoreStore.walletTransactionsLoading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : swagStoreStore.walletTransactionsError ? (
          <Box sx={{ p: 3 }}>
            <CustomTypography variant="body2" color="error">
              {swagStoreStore.walletTransactionsError}
            </CustomTypography>
          </Box>
        ) : swagStoreStore.walletTransactions.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
            }}
          >
            <CustomTypography variant="body2" color="text.secondary">
              No wallet transactions found.
            </CustomTypography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <AppTableHeaderCell>Date</AppTableHeaderCell>
                    <AppTableHeaderCell>Type</AppTableHeaderCell>
                    <AppTableHeaderCell>Source</AppTableHeaderCell>
                    <AppTableHeaderCell>Amount</AppTableHeaderCell>
                    <AppTableHeaderCell>Invoice #</AppTableHeaderCell>
                    <AppTableHeaderCell>Transaction id</AppTableHeaderCell>
                    <AppTableHeaderCell>Balance after</AppTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {swagStoreStore.walletTransactions.map((row) => (
                    <StyledTableRow key={`wallet-txn-${row.id}`}>
                      <AppTableRowCell>
                        {row.created_at
                          ? new Date(row.created_at).toLocaleString()
                          : "—"}
                      </AppTableRowCell>
                      <AppTableRowCell sx={{ textTransform: "capitalize" }}>
                        {row.type || "—"}
                      </AppTableRowCell>
                      <AppTableRowCell>
                        {formatSourceLabel(row.source)}
                      </AppTableRowCell>
                      <AppTableRowCell>{renderAmount(row)}</AppTableRowCell>
                      <AppTableRowCell>
                        {displayOrDash(row.invoice_number)}
                      </AppTableRowCell>
                      <AppTableRowCell>
                        {displayOrDash(row.external_transaction_id)}
                      </AppTableRowCell>
                      <AppTableRowCell>
                        {formatInr(Number(row.balance_after) || 0)}
                      </AppTableRowCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <AppTablePagenation
              fontSize="0.75rem"
              pageNo={swagStoreStore.walletTransactionsPage}
              limit={swagStoreStore.walletTransactionsLimit}
              totalCount={swagStoreStore.walletTransactionsTotalCount}
              showRowsPerPageOptions={false}
              onPageChange={handlePageChange}
              handleRowsChange={() => {}}
            />
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default observer(WalletHistoryDrawer);
